<template>
  <div id="app">

    <!-- 主要内容区 -->
    <main>


      <StaggeredMenu
         position="right"
         :items="menuItems"
         :social-items="socialItems"
         :display-socials="true"
         :display-item-numbering="true"
         menu-button-color="#ffffff"
         open-menu-button-color="#333333"
         :change-menu-color-on-open="true"
         :colors="['rgba(45, 45, 45, 1)', '#666666']"
         accent-color="#cccccc"
         logo-url="/image/logo/NIGHT-SUICHTEL.png"
         additional-logo-url="/image/logo/DAY-SUICHTEL.png"
         @menu-open="handleMenuOpen"
         @menu-close="handleMenuClose"
         :style="{ 
           position: 'fixed', 
           top: '0', 
           right: '0', 
           width: '100%', 
           height: '100%', 
           zIndex: '1000', 
           backgroundColor: 'transparent',
           pointerEvents: menuOpen ? 'auto' : 'none'
         }"
         />
         
      <!-- 英雄区 -->
      <section id="home" class="hero">
        <div class="hero-background">
          <Dither
            v-if="!isMobile"
            :wave-speed="0.05"
            :wave-frequency="3"
            :wave-amplitude="0.3"
            :wave-color="[0.3, 0.3, 0.3]"
            :color-num="4"
            :pixel-size="2"
            :disable-animation="false"
            :enable-mouse-interaction="true"
            :mouse-radius="1"
          />
        </div>
        <div class="hero-content">
          <TextType 
            :text="['SUICHTEL GROP']" 
            :typingSpeed="75" 
            :pauseDuration="1500" 
            :showCursor="true" 
            cursorCharacter="|"
            :initialDelay="500"
            class="hero-title"
          />
          <p class="hero-subtitle">我们创造，变为现实</p>
        </div>
      </section>


      <!-- 名片 -->
      <section id="portfolio" class="portfolio">
        <div class="portfolio-background">
          <LightRays 
            rays-origin="top-center" 
            rays-color="#ffffffff" 
            :rays-speed="1.5" 
            :light-spread="0.8" 
            :ray-length="1.2" 
            :follow-mouse="true" 
            :mouse-influence="0.1" 
            :noise-amount="0.1" 
            :distortion="0.05" 
            class-name="custom-rays" 
          />
        </div>
        <FadeContent 
          :blur="true" 
          :duration="1000" 
          :delay="200" 
          :threshold="0.1" 
          :initial-opacity="0" 
          easing="ease-out" 
          class-name="portfolio-fade-content" 
        >
          <div class="container">
            <h2 class="section-title">名片</h2>
            <div class="profile-card-container">
              <ProfileCard
              name="Atomire Chen"
              title="Engineer"
              handle="suichtel"
              status="Online"
              contact-text="联系我"
              avatar-url="/image/assets/person.png"
              icon-url="/image/assets/iconpattern.png"
              grain-url="/image/assets/grain.webp"
              :show-user-info="true"
              :show-behind-gradient="true"
              :enable-tilt="true"
              @contact-click="handleContactClick"
              />
            </div>

          </div>
        </FadeContent>
      </section>


    </main>

    <!-- 页脚 -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-copyright">© <span id="current-year">2026</span> SuichTEL Grop.</div>
        <img src="/image/logo/NIGHT-SUICHTEL.png" alt="SUICHTEL" class="footer-logo" />
      </div>
    </footer>
  </div>
</template>

<script setup>
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'
import Dither from './component/Dither/Dither.vue'
import StaggeredMenu from './component/StaggeredMenu/StaggeredMenu.vue'
import TextType from './component/TextType/TextType.vue'
import FadeContent from './component/FadeContent/FadeContent.vue'
import LightRays from './component/LightRays/LightRays.vue'
import ProfileCard from './component/ProfileCard/ProfileCard.vue'

const menuOpen = ref(false)

// 检测是否为移动设备
const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const menuItems = [
  { label: '首页', ariaLabel: '返回首页', link: '#home' },
  { label: '联系我们', ariaLabel: '获取联系方式', link: '#portfolio' }
]

const socialItems = [
  { label: 'Github', link: 'https://gh-prof.suichtel.com' }
]


const handleMenuOpen = () => {
  console.log('菜单已打开')
  menuOpen.value = true
}
const handleMenuClose = () => {
  console.log('菜单已关闭')
  menuOpen.value = false
}

const handleContactClick = () => {
    window.location.href = 'mailto:caq@suichtel.com';
};

// 全屏滚动逻辑
let isScrolling = false
let currentSection = 0
const sections = ['#home', '#portfolio']

const scrollToSection = (index) => {
  if (index < 0 || index >= sections.length) return
  
  const element = document.querySelector(sections[index])
  if (element) {
    element.scrollIntoView()
    currentSection = index
  }
}

const handleWheel = (event) => {
  if (isScrolling) return
  
  isScrolling = true
  
  if (event.deltaY > 0) {
    // 向下滚动
    scrollToSection(currentSection + 1)
  } else {
    // 向上滚动
    scrollToSection(currentSection - 1)
  }
  
  setTimeout(() => {
    isScrolling = false
  }, 800)
}

onMounted(() => {
  // 刷新页面时自动回到首页
  window.scrollTo(0, 0)
  
  window.addEventListener('wheel', handleWheel, { passive: false })
})

onUnmounted(() => {
  window.removeEventListener('wheel', handleWheel)
})
</script>

<style scoped>
/* 基础样式 */
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #0a0a0a;
  color: #e0e0e0;
  font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
  overflow-x: hidden;
}

/* 禁用侧边滚动条 */
html {
  overflow-x: hidden;
}

body {
  overflow-x: hidden;
}

/* 隐藏滚动条但保持滚动功能 */
::-webkit-scrollbar {
  display: none;
}

* {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;      /* Firefox */
}

/* 主要内容 */
main {
  flex: 1;
}

/* 英雄区 */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 100;
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  color: #ffffff;
}

.hero-subtitle {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: #aaaaaa;
}

/* 通用容器 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
}

/* 区段标题 */
.section-title {
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1rem;
  color: #ffffff;
}

.section-description {
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  color: #aaaaaa;
  line-height: 1.6;
}

/* 作品集 */
.portfolio {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.portfolio-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  justify-content: top;
  align-items: top;
}

.portfolio-fade-content {
  position: relative;
  z-index: 2;
  width: 100%;
}

.profile-card-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 3rem 0;
  width: 100%;
}



/* 页脚 */
.footer {
  background-color: #111111;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  box-sizing: border-box;
}

.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  max-width: 1200px;
  width: 100%;
}

.footer-copyright {
  font-size: 0.9rem;
  color: #aaaaaa;
  text-align: center;
}

.footer-logo {
  height: 30px;
  width: auto;
  display: block;
}

/* 响应式设计 - 平板 */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1rem;
    padding: 0 1rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .section-description {
    font-size: 0.95rem;
    padding: 0 1rem;
  }

  .container {
    padding: 3rem 1.5rem;
  }

  .profile-card-container {
    margin: 2rem 0;
  }

  .footer {
    padding: 1rem 1.5rem;
    min-height: 100px;
  }
}

/* 响应式设计 - 手机 */
@media (max-width: 480px) {
  .hero-title {
    font-size: 1.8rem;
  }

  .hero-subtitle {
    font-size: 0.9rem;
    padding: 0 0.5rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .section-description {
    font-size: 0.85rem;
    padding: 0 0.5rem;
  }

  .container {
    padding: 2rem 1rem;
  }

  .profile-card-container {
    margin: 1.5rem 0;
  }

  .footer {
    padding: 1rem;
    min-height: 80px;
  }

  .footer-copyright {
    font-size: 0.8rem;
  }

  .footer-logo {
    height: 25px;
  }
}
</style>